import React, {useState} from "react";
import {convertToCSV, funcOrValOr, pickKeys} from "../tools/CommonJS";
import {downloadUri} from "../tools/CommonHtml";
import {Button, TextInput} from "../tools/Inputs";
import {FormBox} from "../tools/UIDialog";
import {msgBox, msgBoxEx, queryOrErr, useFetchData} from "./AppContext";
import {mockData} from "./MockData";
import {dispUserName} from "./CommonUIComponenets";
import {tailCheck} from "../tools/CommonReact";
import {icn} from "../tools/UIIcons";

export const exportMsg = <div className="leading-6">Export is in progress. When it's ready, you can download it from the Notifications menu — click the <span className="inline-flex relative top-0.5 fill-title">{icn.bell}</span> icon.</div>;
export const socketNotifs = "notifs_updated";

export function ExportButton({title = "", disabled, className = "", ...exportProps}) {
    return <Button
        h="Export" title={title ?? "Export list to a CSV file..."}
        data-test="export-button"
        className={tailCheck(className, "text-sm", "text-prm-light")}
        onClick={() => askExport(exportProps)}
        disabled={!disabled || typeof disabled === "string"? disabled : "No items selected."}/>;
}

export function askExport({exportName, api, params, user, ext}) {
    msgBoxEx({
        Elm: FormBox, autoFocus: false, btnOk: "Generate Export", className: "dlg-lg",
        form: [{label: "File name", field: "exportName", cmpt: TextInput, classNameCol2: "grow",
            props: {autoFocus: true, placeholder: "export file name"}}],
        formInit: {exportName: nameExport(funcOrValOr(exportName), ext, user)},
        checkOk: st => {
            if (!st.exportName) return "Value cannot be empty.";
            if (!(/^[a-zA-Z0-9_\-() ]+\.csv$/).test(st.exportName)) return `File name can only contain letters, numbers, underscores, dashes, and parentheses.`;
        },
        onOk: st => queryOrErr({
            api, rsp: () => msgBox(exportMsg),
            params: {
                ...funcOrValOr(params),
                exportName: st.exportName,
                socket: socketNotifs
            }
        })
    });
}

export function nameExport(name, ext, user) {
    const dt = new Date(Date.now() - new Date().getTimezoneOffset() * 60 * 1000).toISOString().split(/[T:.]/);
    return `${name} - ${!user ? "" : `by ${dispUserName(user)} `}${dt[0]} ${dt[1]}_${dt[2]}`
        .replace(/[^a-zA-Z0-9_\-() ]/g, "_") + (ext ?? ".csv");
}

export function downloadFile({api, params, acceptTypes, fQuery, filename, ...props}) {
    (fQuery ?? queryOrErr)({
        api, params, ...props,
        onRsp: rsp => {
            if (acceptTypes?
                !acceptTypes.includes(rsp?.headers.get("content-type")) :
                rsp?.headers.get("content-type")?.startsWith("application/json"))
                return false;
            return rsp.blob().then(blob => downloadUri({blob, filename}));
        }
    });
}

export const downloadFileFromData = (data, filename, keys) =>
    downloadUri({uri: "data:text/csv;charset=utf-8,%EF%BB%BF" +encodeURIComponent(convertToCSV(data, keys)), filename: nameExport(filename)});

// Shared download logic for notification payloads (sensor logs + offline exports), reused by the Health page and the NavBar bell menu.
export const downloadNotifPayload = notif =>
    queryOrErr({
        api: "/api/v3/notif_get-payload",
        params: {...pickKeys(notif, "id", "type"), socket: socketNotifs},
        rsp: r => window.open(r.url, "_blank")      // opens a new tab instead of using downloadFile. couldn't overcome the sent content-type when using the 302 redirect method. @dim: seaweed builds the signature using several headers, Content-Type is one of them  if it's missing, it falls back to "" (and it is missing when we sign, so it uses "") if UI specifies Content-Type to anything except "" (which it does), signature is invalid.
    });

// Two display styles for the same download action; the logic above is shared.
const notifDownloadStyles = {
    icon: {className: "icon-download px-3 shrink-0 text-lg font-bold text-prm-light min-w-11", dataTest: "log-download-button"},
    labeled: {className: "text-xs w-fit pr-1 flex-shrink-0 text-prm-light", dataTest: "download-btn", h: <div><i className="icon-download"/> Download</div>},
};

export function NotifDownloadButton({notif, variant = "icon", title, placeholder = null, stopPropagation}) {
    if (!notif.hasPayload && notif.type !== "offlineExport") return placeholder;      // Nothing to download yet — offlineExport always shows the button (disabled until ready).
    const {className, dataTest, h} = notifDownloadStyles[variant];
    return <Button
        className={className} data-test={dataTest} {...h && {h}} {...title && {title}}
        disabled={!notif.hasPayload && "In progress..."}
        onClick={e => {
            stopPropagation && e.stopPropagation();
            downloadNotifPayload(notif);
        }}/>;
}

// Shared notifications state (list fetch + dismiss) for the Health page and the NavBar bell menu.
// dismiss(ids, onDone) toggles a local dismissing flag folded into isLoading, then refreshes the list.
export function useNotifications() {
    const [isDismissing, setIsDismissing] = useState(false);
    const {rsp, refresh, isLoading: isFetching} = useFetchData({api: "/api/v2/notif_get-list", socket: socketNotifs, alt: mockData.notifGetList});
    const dismiss = (ids, onDone) =>
        queryOrErr({
            api: "/api/v2/notif_dismiss", alt: {}, onLoading: setIsDismissing,
            params: {ids, socket: socketNotifs},
            rsp: () => {
                onDone?.();
                refresh();
            },
        });
    return {rsp, refresh, isLoading: isFetching || isDismissing, dismiss};
}
